import { Button, Paper, Toolbar, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {
  ConfirmDialog,
  ResponsiveIconButton,
  RouterLink,
  SnackbarContext,
  SortableTable,
  TableControls,
} from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import { MenuCategory, MenuSection } from 'interfaces';
import * as React from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { useParams, useRouteMatch } from 'react-router-dom';
import { MenuCategoryService } from 'services';
import { SaveMenuCategoryDialog } from '../components/SaveMenuCategoryDialog';

interface Params {
  id: string;
}

export const MenuCategoryView: React.FC = () => {
  const match = useRouteMatch<Params>();
  const { id: menuSectionId } = useParams<Params>();

  const snackbar = React.useContext(SnackbarContext);

  const [modalOpen, setModalOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [menuSection, setMenuSection] = React.useState<MenuSection>();
  const [selectedMenuCategory, setSelectedMenuCategory] = React.useState<
    MenuCategory
  >();

  const loadMenuCategories = React.useCallback(
    async function loadMenuCategories(id: string) {
      try {
        const response = await MenuCategoryService.get(id);
        setMenuSection(response);
      } catch (error) {
        snackbar.error(error);
      }
    },
    [snackbar],
  );

  React.useEffect(() => {
    loadMenuCategories(menuSectionId);
  }, [menuSectionId, loadMenuCategories]);

  const columns: SortableTableHeader[] = [
    { key: 'title', label: 'Title' },
    { key: 'rank', label: 'Rank' },
    { key: 'controls', sortable: false },
  ];

  const rows =
    menuSection?.menuCategories.map((category) => ({
      key: category.id.toString(),
      cells: [
        {
          key: 'title',
          display: (
            <Button
              component={RouterLink}
              to={`/menu-sections/${menuSectionId}/categories/${category.id}/items`}
              variant="text"
              size="small"
              style={{ minWidth: 'auto' }}
              color="secondary"
            >
              {category.name}
            </Button>
          ),
          sortValue: category.name,
        },
        { key: 'rank', display: category.rank },
        {
          key: 'controls',
          display: (
            <TableControls
              onEdit={(e) => {
                setModalOpen(true);
                setSelectedMenuCategory(category);
              }}
              onDelete={(e) => {
                setConfirmOpen(true);
                setSelectedMenuCategory(category);
              }}
            />
          ),
        },
      ],
    })) ?? [];

  return (
    <React.Fragment>
      {match && <BreadcrumbsItem to={match.url}> Categories</BreadcrumbsItem>}
      <Paper>
        <Toolbar style={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {menuSection ? `${menuSection.title} - ` : ''} Categories
          </Typography>

          <ResponsiveIconButton
            onClick={(e) => {
              setSelectedMenuCategory(undefined);
              setModalOpen(true);
            }}
            color="primary"
            icon={AddIcon}
          >
            Add Menu Category
          </ResponsiveIconButton>
        </Toolbar>

        <SortableTable
          columns={columns}
          rows={rows}
          emptyTableText="No menu category available yet."
          emptyTableCreateAction={(e) => setModalOpen(true)}
          disablePagination
        />

        <SaveMenuCategoryDialog
          menuCategory={selectedMenuCategory}
          categories={menuSection?.menuCategories ?? []}
          menuSectionId={parseInt(menuSectionId, 10)}
          refresh={() => loadMenuCategories(menuSectionId)}
          dialogProps={{
            open: modalOpen,
            onClose: (e) => setModalOpen(false),
            fullWidth: true,
            maxWidth: 'xs',
          }}
        />

        <ConfirmDialog
          title="Confirm"
          content="Are you sure you would like to delete this category?"
          onConfirm={deleteMenuCategory}
          dialogProps={{
            open: confirmOpen,
            onClose: (e) => {
              setConfirmOpen(false);
              setSelectedMenuCategory(undefined);
            },
          }}
        />
      </Paper>
    </React.Fragment>
  );

  async function deleteMenuCategory() {
    try {
      if (!selectedMenuCategory) {
        return;
      }

      const response = await MenuCategoryService.delete(
        selectedMenuCategory.id,
      );

      if (!response) {
        return;
      }

      loadMenuCategories(menuSectionId);
      snackbar.open('Menu category has been deleted successfully');
    } catch (error) {
      snackbar.error(error);
    }
  }
};
